import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Handshake, ExternalLink } from "lucide-react";
import { api, COLORS } from "@/lib/api";

const initials = (name) => {
  const clean = name.replace(/\(.*?\)/g, "").trim();
  return clean.split(" ").filter((w) => w.length > 2).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || name.slice(0, 2).toUpperCase();
};

const Alianzas = () => {
  const [ALIANZAS_GRUPOS, setGrupos] = useState([]);
  useEffect(() => {
    api.get("/alianzas").then((r) => {
      const map = new Map();
      r.data.forEach((a) => {
        if (!map.has(a.grupo)) map.set(a.grupo, []);
        map.get(a.grupo).push(a);
      });
      setGrupos([...map.entries()].map(([grupo, aliados]) => ({ grupo, aliados })));
    }).catch(() => {});
  }, []);
  const total = ALIANZAS_GRUPOS.reduce((n, g) => n + g.aliados.length, 0);

  return (
    <div data-testid="subpage-alianzas" className="max-w-6xl mx-auto px-6 md:px-12 py-16">
      <Link
        to="/conocenos"
        data-testid="subpage-back"
        className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/60 hover:text-brand-ink mb-8"
      >
        <ArrowLeft size={16} /> Conócenos
      </Link>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="inline-block w-16 h-1.5 rounded-sm mb-6" style={{ background: COLORS.magenta }} />
        <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-brand-ink">
          Alianzas
        </h1>
        <p className="mt-5 text-lg md:text-xl text-brand-ink/70 leading-relaxed max-w-2xl">
          No caminamos solas. Tejemos redes con más de {total} organizaciones, instituciones, fondos
          y colectivas que comparten nuestra causa por la equidad de género.
        </p>
      </motion.div>

      {/* Alianza destacada: Juntes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="mt-12 bg-brand-ink rounded-sm p-8 md:p-10"
      >
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-sm flex items-center justify-center" style={{ background: COLORS.lime }}>
            <Handshake size={22} className="text-brand-ink" strokeWidth={2.2} />
          </span>
          <span className="text-xs uppercase tracking-[0.18em] font-semibold text-white/60">
            Formamos parte de
          </span>
        </div>
        <h2 className="mt-5 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white">
          Juntes <span className="text-gradient">Narramos</span>
        </h2>
        <p className="mt-3 text-white/70 leading-relaxed max-w-3xl">
          Juntes Narramos es la alianza de la que formamos parte junto con{" "}
          <span className="font-semibold text-white">Presentes</span>,{" "}
          <span className="font-semibold text-white">Balance A.C.</span>,{" "}
          <span className="font-semibold text-white">Malvestida</span> y{" "}
          <span className="font-semibold text-white">Volcánicas</span>, un consorcio que impulsa
          narrativas, liderazgo e incidencia de las juventudes.
        </p>
      </motion.div>

      {/* Alianzas destacadas adicionales */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          data-testid="alianza-girls-not-brides"
          className="bg-white border border-[#E5E5E5] rounded-sm p-7"
        >
          <span className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: COLORS.magenta }}>
            Formamos parte de
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink">
            Girls Not Brides
          </h2>
          <p className="mt-3 text-brand-ink/70 leading-relaxed">
            Somos parte de Girls Not Brides, la alianza global contra el matrimonio infantil. Contamos con un{" "}
            <span className="font-semibold text-brand-ink">comité interno</span> que impulsa esfuerzos, con
            perspectiva de juventudes, en torno a los matrimonios y uniones tempranas y forzadas.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          data-testid="alianza-red-steam"
          className="bg-white border border-[#E5E5E5] rounded-sm p-7"
        >
          <span className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: COLORS.orange }}>
            Formamos parte de
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink">
            Red de Ecosistemas STEAM
          </h2>
          <p className="mt-3 text-brand-ink/70 leading-relaxed">
            La red de ecosistemas STEM/STEAM que promueve UNICEF busca ser un entorno dinámico que reúne a
            diversos actores, recursos y prácticas para promover una educación de calidad en las disciplinas
            STEM con perspectiva de género.
          </p>
          <a
            href="https://sites.google.com/view/redfuturasstemcdmx/inicio"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="red-steam-link"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
            style={{ color: COLORS.orange }}
          >
            Conoce la red <ExternalLink size={14} />
          </a>
        </motion.div>
      </div>

      {/* Muro de aliados */}
      {ALIANZAS_GRUPOS.map((g, gi) => (
        <section key={g.grupo} className="mt-14">
          <div className="flex items-center gap-3">
            <span className="w-8 h-1.5 rounded-sm" style={{ background: g.color }} />
            <h2 className="font-heading text-xl md:text-2xl font-semibold tracking-tight text-brand-ink">
              {g.grupo}
            </h2>
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {g.aliados.map((a, i) => (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 4) * 0.05 }}
                data-testid={`aliado-${gi}-${i}`}
                className="group bg-white border border-[#E5E5E5] rounded-sm p-5 flex items-center gap-3 transition-all duration-300 hover:-translate-y-1"
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = g.color)}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E5E5E5")}
              >
                {a.logo ? (
                  <img src={a.logo} alt={a.name} className="w-11 h-11 object-contain flex-shrink-0" />
                ) : (
                  <span
                    className="w-11 h-11 rounded-sm flex items-center justify-center flex-shrink-0 font-heading font-bold text-white text-sm"
                    style={{ background: g.color }}
                  >
                    {initials(a.name)}
                  </span>
                )}
                <span className="font-semibold text-sm text-brand-ink leading-tight">{a.name}</span>
              </motion.div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default Alianzas;
